// src/pages/ClassroomMain.js
import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import { getClassroomsForUser } from '../lib/supabaseClassroomAPI';
import { supabase } from '../supabaseClient';

function SkeletonCard() {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 animate-pulse space-y-3">
      <div className="h-5 w-2/3 bg-gray-100 rounded" />
      <div className="h-4 w-1/3 bg-gray-100 rounded" />
    </div>
  );
}

export default function ClassroomMain() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [joinedClassrooms, setJoinedClassrooms] = useState([]);
  const [ownedClassrooms, setOwnedClassrooms] = useState([]);
  const [error, setError] = useState('');

  // UI
  const [q, setQ] = useState('');
  const [sortJoined, setSortJoined] = useState('name'); // 'name' | 'recent'
  const [sortOwned, setSortOwned] = useState('recent'); // 'name' | 'recent'
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      const [joined, ownedRes] = await Promise.all([
        getClassroomsForUser(user.id),
        supabase.from('classrooms').select('id, name, code, created_at').eq('host_id', user.id),
      ]);
      if (ownedRes.error) throw ownedRes.error;

      // Basic normalization in case API returns null
      setJoinedClassrooms(Array.isArray(joined) ? joined : []);
      setOwnedClassrooms(Array.isArray(ownedRes.data) ? ownedRes.data : []);
    } catch (err) {
      console.error(err);
      setError('Failed to load classrooms.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  // Filter out owned from joined (avoid duplicates)
  const filteredJoined = useMemo(() => {
    const ownedIds = new Set(ownedClassrooms.map((o) => o.id));
    let rows = (joinedClassrooms || []).filter((j) => !ownedIds.has(j.id));
    if (q.trim()) {
      const needle = q.toLowerCase();
      rows = rows.filter((r) => (r.name || '').toLowerCase().includes(needle));
    }
    if (sortJoined === 'name') {
      rows.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else {
      rows.sort(
        (a, b) =>
          new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
      );
    }
    return rows;
  }, [joinedClassrooms, ownedClassrooms, q, sortJoined]);

  const filteredOwned = useMemo(() => {
    let rows = [...(ownedClassrooms || [])];
    if (q.trim()) {
      const needle = q.toLowerCase();
      rows = rows.filter((r) => (r.name || '').toLowerCase().includes(needle));
    }
    if (sortOwned === 'name') {
      rows.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else {
      rows.sort(
        (a, b) =>
          new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
      );
    }
    return rows;
  }, [ownedClassrooms, q, sortOwned]);

  const copyCode = async (id, code) => {
    try {
      await navigator.clipboard.writeText(code || '');
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1200);
    } catch {
      // ignore
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-6xl mx-auto space-y-10">
      {/* Header / Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Classrooms</h2>
          <p className="text-sm text-gray-600">Join an existing class or manage your own.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/classrooms/join">
            <button className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded">
              Join Classroom
            </button>
          </Link>
          {user?.role === 'teacher' && (
            <Link to="/classrooms/create">
              <button className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded">
                Create Classroom
              </button>
            </Link>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="grid sm:grid-cols-6 gap-3">
          <div className="sm:col-span-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              placeholder="Search by classroom name"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="sm:col-span-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">Sort Joined</label>
            <select
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={sortJoined}
              onChange={(e) => setSortJoined(e.target.value)}
            >
              <option value="name">Name A–Z</option>
              <option value="recent">Recently added</option>
            </select>
          </div>
          <div className="sm:col-span-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">Sort Owned</label>
            <select
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={sortOwned}
              onChange={(e) => setSortOwned(e.target.value)}
            >
              <option value="recent">Recently created</option>
              <option value="name">Name A–Z</option>
            </select>
          </div>
        </div>
      </div>

      {/* Joined Classrooms */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-gray-900">Joined Classrooms</h3>
          {loading ? null : (
            <span className="text-sm text-gray-600">
              {filteredJoined.length} enrolled
            </span>
          )}
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={`j-${i}`} />
            ))}
          </div>
        ) : filteredJoined.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-4 text-gray-700">
            You have not joined any classrooms yet.
          </div>
        ) : (
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredJoined.map((cls) => (
              <li
                key={cls.id}
                className="rounded-lg border border-gray-200 bg-white p-4 flex items-start justify-between gap-3"
              >
                <div className="min-w-0">
                  <Link
                    to={`/classrooms/student/${cls.id}`}
                    className="text-base font-semibold text-blue-700 hover:underline block truncate"
                    title={cls.name}
                  >
                    {cls.name}
                  </Link>
                  <p className="text-xs text-gray-600 mt-1">
                    Joined class
                  </p>
                </div>
                <Link
                  to={`/classrooms/student/${cls.id}`}
                  className="shrink-0 bg-gray-800 hover:bg-gray-700 text-white px-3 py-1.5 rounded text-sm"
                >
                  Open
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Owned Classrooms (Teachers) */}
      {user?.role === 'teacher' && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-gray-900">My Classrooms</h3>
            {loading ? null : (
              <span className="text-sm text-gray-600">
                {filteredOwned.length} created
              </span>
            )}
          </div>

          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <SkeletonCard key={`o-${i}`} />
              ))}
            </div>
          ) : filteredOwned.length === 0 ? (
            <div className="rounded-lg border border-gray-200 bg-white p-4 text-gray-700">
              You have not created any classrooms yet.
            </div>
          ) : (
            <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredOwned.map((cls) => (
                <li
                  key={cls.id}
                  className="rounded-lg border border-gray-200 bg-white p-4 flex flex-col gap-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-base font-semibold text-gray-900 truncate" title={cls.name}>
                      {cls.name}
                    </span>
                    <Link
                      to={`/classrooms/manage/${cls.id}`}
                      className="shrink-0 text-blue-700 hover:underline text-sm"
                    >
                      Manage
                    </Link>
                  </div>

                  <div className="rounded-md border border-dashed border-gray-300 p-3 bg-gray-50">
                    <p className="text-xs text-gray-600 mb-1">Join Code</p>
                    <div className="flex items-center justify-between gap-2">
                      <code className="text-sm text-gray-900 truncate">{cls.code || '—'}</code>
                      <button
                        onClick={() => copyCode(cls.id, cls.code)}
                        className="text-xs px-2 py-1 rounded border hover:bg-gray-100"
                      >
                        {copiedId === cls.id ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-2">
                    <Link
                      to={`/classrooms/manage/${cls.id}`}
                      className="bg-gray-800 hover:bg-gray-700 text-white px-3 py-1.5 rounded text-sm"
                    >
                      Open
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
    </div>
  );
}
